import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, RefreshCw, Edit2, X, Check, AlertTriangle,
  ChevronDown, ChevronUp, Loader2, HeartPulse, Settings, Search,
} from 'lucide-react';
import {
  getApis, createApi, deleteApi,
  getTenants, createTenant, updateTenant, deleteTenant,
  probe,
} from '../../services/healthCheckApi';

// ── Helpers ───────────────────────────────────────────────────────────────────

const stripBranch = (ref) => ref?.replace(/^refs\/heads\//, '') ?? ref;

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

const timeAgo = (ts) => {
  if (!ts) return null;
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
};

const URL_TEMPLATES = [
  { apiName: 'Blackbird API',       template: (t) => `https://${t}-scus-app-api.azurewebsites.net/api/v1/info` },
  { apiName: 'Inventory API',       template: (t) => `https://${t}-inventory-api.azurewebsites.net/api/info` },
  { apiName: 'Company API',         template: (t) => `https://${t}-company-api.azurewebsites.net/api/info` },
  { apiName: 'Contacts API',        template: (t) => `https://${t}-contacts-api.azurewebsites.net/api/info` },
  { apiName: 'Bearcat API',         template: (t) => `https://${t}-api.azurewebsites.net/api/info` },
  { apiName: 'Bearcat Liveauction', template: (t) => `https://${t}-simulcast-auctionengine.azurewebsites.net/api/info` },
];

const ENV_COLORS = [
  '#10b981', '#6366f1', '#f59e0b', '#ec4899', '#14b8a6',
  '#8b5cf6', '#0ea5e9', '#f97316', '#84cc16', '#06b6d4',
];

// ── Design Sub-components ─────────────────────────────────────────────────────

function StatusDot({ status }) {
  if (status === 'healthy') {
    return (
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
      </span>
    );
  }
  if (status === 'down') {
    return <span className="inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />;
  }
  return <span className="inline-flex rounded-full h-2.5 w-2.5 bg-gray-300 dark:bg-slate-600" />;
}

function BranchBadge({ branch, color }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold tracking-tight"
      style={{
        fontFamily: "'JetBrains Mono', monospace",
        background: color ? `${color}12` : '#dbeafe',
        color: color || '#2563eb',
        border: `1px solid ${color ? `${color}30` : '#bfdbfe'}`,
      }}
    >
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="8" cy="4" r="2.5" />
        <circle cx="8" cy="12" r="2.5" />
        <path d="M8 6.5v3" />
      </svg>
      {branch}
    </span>
  );
}

function StatusLabel({ status }) {
  const map = {
    healthy: 'text-green-700 bg-green-50 dark:bg-green-950',
    down: 'text-red-700 bg-red-50 dark:bg-red-950',
  };
  const label = status === 'healthy' ? 'Healthy' : status === 'down' ? 'Down' : '—';
  if (!map[status]) return <span className="text-gray-300 dark:text-slate-600">—</span>;
  return (
    <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${map[status]}`}>
      {label}
    </span>
  );
}

// ── Manage APIs Modal ─────────────────────────────────────────────────────────

function ManageApisModal({ apis, onClose, onRefreshApis }) {
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    setError('');
    try {
      await createApi(name);
      setNewName('');
      await onRefreshApis();
    } catch (e) {
      setError(e.message || 'Failed to add API');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteApi(id);
      await onRefreshApis();
    } catch (e) {
      setError(e.message || 'Failed to delete API');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Manage API Catalog</h2>
          <button onClick={onClose} className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-gray-300">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3 max-h-80 overflow-y-auto">
          {apis.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-4">No APIs added yet.</p>
          )}
          {apis.map((api) => (
            <div key={api.id} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-slate-800 last:border-0">
              <span className="text-sm text-gray-700 dark:text-gray-300">{api.name}</span>
              <button
                onClick={() => handleDelete(api.id)}
                disabled={deletingId === api.id}
                className="text-red-400 hover:text-red-600 disabled:opacity-50"
              >
                {deletingId === api.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              </button>
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700">
          {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
          <div className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="API name (e.g. Blackbird API)"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleAdd}
              disabled={saving || !newName.trim()}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tenant Form Modal ─────────────────────────────────────────────────────────

function TenantFormModal({ apis, existing, onClose, onSaved }) {
  const [name, setName] = useState(existing?.name ?? '');
  const [rows, setRows] = useState(
    existing?.tenantApis?.map((ta) => ({
      key: ta.id,
      apiId: ta.apiId,
      url: ta.url,
      checkState: null,
    })) ?? []
  );
  const [tenantKey, setTenantKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleAutoFill = () => {
    const t = tenantKey.trim().toLowerCase();
    if (!t) return;
    if (!name.trim()) setName(t.toUpperCase());
    const newRows = URL_TEMPLATES.map((tmpl) => {
      const matchedApi = apis.find(
        (a) => a.name.toLowerCase() === tmpl.apiName.toLowerCase()
      );
      return {
        key: Date.now() + Math.random(),
        apiId: matchedApi?.id ?? apis[0]?.id ?? '',
        url: tmpl.template(t),
        checkState: null,
      };
    });
    setRows(newRows);
  };

  const addRow = () =>
    setRows((r) => [...r, { key: Date.now(), apiId: apis[0]?.id ?? '', url: '', checkState: null }]);

  const removeRow = (key) => setRows((r) => r.filter((row) => row.key !== key));

  const updateRow = (key, field, value) =>
    setRows((r) => r.map((row) => (row.key === key ? { ...row, [field]: value, checkState: null } : row)));

  const checkUrl = async (key, url) => {
    if (!url.trim()) return;
    setRows((r) => r.map((row) => (row.key === key ? { ...row, checkState: 'loading' } : row)));
    try {
      const result = await probe(url.trim());
      setRows((r) =>
        r.map((row) =>
          row.key === key
            ? { ...row, checkState: result.success ? { ok: true, data: result.data } : { ok: false, error: result.error } }
            : row
        )
      );
    } catch {
      setRows((r) =>
        r.map((row) =>
          row.key === key ? { ...row, checkState: { ok: false, error: 'Request failed' } } : row
        )
      );
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Environment name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: name.trim(),
        tenantApis: rows
          .filter((r) => r.apiId && r.url.trim())
          .map((r) => ({ apiId: Number(r.apiId), url: r.url.trim() })),
      };
      if (existing) {
        await updateTenant(existing.id, payload);
      } else {
        await createTenant(payload);
      }
      onSaved();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            {existing ? 'Edit Environment' : 'Add Environment'}
          </h2>
          <button onClick={onClose} className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-gray-300">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Environment name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. UAT, Production - Permian"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tenant name</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={tenantKey}
                onChange={(e) => setTenantKey(e.target.value)}
                placeholder="e.g. tac, permian"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handleAutoFill}
                disabled={!tenantKey.trim() || apis.length === 0}
                className="px-4 py-2 text-sm font-medium bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-40 whitespace-nowrap"
              >
                Auto-fill URLs
              </button>
            </div>
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Generates URLs for 6 standard APIs using tenant naming conventions</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">APIs</label>
              <button
                onClick={addRow}
                disabled={apis.length === 0}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-40"
              >
                <Plus size={13} /> Add API
              </button>
            </div>

            {apis.length === 0 && (
              <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                No APIs in catalog yet. Use "Manage APIs" to add them first.
              </p>
            )}

            <div className="space-y-3">
              {rows.map((row) => (
                <div key={row.key} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <select
                      value={row.apiId}
                      onChange={(e) => updateRow(row.key, 'apiId', e.target.value)}
                      className="w-44 px-2 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {apis.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>

                    <input
                      type="text"
                      value={row.url}
                      onChange={(e) => updateRow(row.key, 'url', e.target.value)}
                      placeholder="https://...azurewebsites.net/api/v1/info"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />

                    <button
                      onClick={() => checkUrl(row.key, row.url)}
                      disabled={!row.url.trim() || row.checkState === 'loading'}
                      className="px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-40 flex items-center gap-1 text-gray-600 dark:text-gray-400"
                      title="Validate URL"
                    >
                      {row.checkState === 'loading' ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Check size={14} />
                      )}
                      Check
                    </button>

                    <button onClick={() => removeRow(row.key)} className="text-red-400 hover:text-red-600 p-1">
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {row.checkState && row.checkState !== 'loading' && (
                    <div className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 ${
                      row.checkState.ok
                        ? 'bg-green-50 dark:bg-green-950 text-green-700'
                        : 'bg-red-50 dark:bg-red-950 text-red-700'
                    }`}>
                      {row.checkState.ok ? (
                        <>
                          <Check size={12} />
                          {row.checkState.data?.application?.name} v{row.checkState.data?.application?.version}
                          {' — '}
                          {stripBranch(row.checkState.data?.buildInfo?.sourceBranch)}
                        </>
                      ) : (
                        <>{row.checkState.error}</>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700">
          {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 dark:text-gray-300">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {existing ? 'Save Changes' : 'Add Environment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Environment Card ──────────────────────────────────────────────────────────

function EnvCard({ tenant, color, isExpanded, onToggle, onEdit, onDelete, onRefresh, probeResults, refreshing }) {
  const results = probeResults ?? {};
  const hasResults = Object.keys(results).length > 0;
  const apiCount = tenant.tenantApis?.length ?? 0;

  // Compute health stats from probe results
  const healthyCount = hasResults
    ? Object.values(results).filter((r) => r?.success).length
    : 0;
  const downCount = hasResults
    ? Object.values(results).filter((r) => r && !r.success).length
    : 0;
  const allHealthy = hasResults && healthyCount === apiCount && downCount === 0;
  const healthPercent = apiCount > 0 && hasResults ? (healthyCount / apiCount) * 100 : 0;

  // Branch analysis
  const branches = hasResults
    ? Object.values(results)
        .filter((r) => r?.success && r.data?.buildInfo?.sourceBranch)
        .map((r) => stripBranch(r.data.buildInfo.sourceBranch))
    : [];
  const uniqueBranches = [...new Set(branches)];
  const hasMismatch = uniqueBranches.length > 1;
  const majorityBranch = hasMismatch
    ? branches.reduce((acc, b) => { acc[b] = (acc[b] || 0) + 1; return acc; }, {})
    : {};
  const topBranch = hasMismatch
    ? Object.entries(majorityBranch).sort((a, b) => b[1] - a[1])[0]?.[0]
    : uniqueBranches[0];

  const overallStatus = !hasResults ? 'pending' : allHealthy ? 'healthy' : 'down';

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden transition-shadow duration-300"
      style={{ boxShadow: isExpanded ? '0 8px 32px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.04)' }}>

      {/* Color accent bar */}
      <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${color}, ${color}80)` }} />

      {/* Card header */}
      <div className="px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          {/* Left: avatar + info */}
          <div className="flex items-center gap-3.5 min-w-0 flex-1">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-[15px] font-extrabold"
              style={{
                background: `linear-gradient(135deg, ${color}18, ${color}08)`,
                border: `1.5px solid ${color}25`,
                color: color,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {tenant.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h3
                  className="text-base font-bold text-gray-900 dark:text-gray-100 truncate"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {tenant.name}
                </h3>
                <StatusDot status={overallStatus} />
                {hasMismatch && (
                  <AlertTriangle size={15} className="text-red-500 shrink-0 animate-blink" />
                )}
              </div>
              <div className="flex items-center gap-2.5 mt-1">
                {topBranch && <BranchBadge branch={topBranch} color={color} />}
                <span className="text-xs text-gray-400 dark:text-slate-500 font-medium">
                  {apiCount} API{apiCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Right: health bar + actions */}
          <div className="flex items-center gap-3 shrink-0">
            {/* Mini health bar */}
            {hasResults && (
              <div className="flex items-center gap-2">
                <div className="w-16 h-1.5 rounded-full bg-gray-100 dark:bg-slate-800 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${healthPercent}%`,
                      background: allHealthy ? '#22c55e' : `linear-gradient(90deg, #22c55e ${healthPercent}%, #ef4444)`,
                    }}
                  />
                </div>
                <span
                  className="text-xs font-bold min-w-[36px] text-right"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: allHealthy ? '#22c55e' : '#f59e0b',
                  }}
                >
                  {healthyCount}/{apiCount}
                </span>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => onEdit(tenant)}
                className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 rounded-lg transition-colors"
                title="Edit"
              >
                <Edit2 size={15} />
              </button>
              <button
                onClick={onRefresh}
                disabled={refreshing}
                className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-950 rounded-lg transition-colors disabled:opacity-50"
                title="Refresh status"
              >
                <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={onToggle}
                className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-all"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                  style={{ color: isExpanded ? color : undefined }}>
                  <ChevronDown size={15} />
                </div>
              </button>
              <button
                onClick={() => onDelete(tenant.id)}
                className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition-colors"
                title="Delete environment"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible API table */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: isExpanded ? 600 : 0 }}
      >
        <div className="border-t border-gray-100 dark:border-slate-800">
          {!hasResults && !refreshing && apiCount > 0 && (
            <div className="px-5 py-6 text-center text-sm text-gray-400 dark:text-slate-500">
              Click <RefreshCw size={12} className="inline" /> to load API status
            </div>
          )}

          {!hasResults && !refreshing && apiCount === 0 && (
            <div className="px-5 py-6 text-center text-sm text-gray-400 dark:text-slate-500">
              No APIs configured for this environment.
            </div>
          )}

          {refreshing && !hasResults && (
            <div className="px-5 py-6 flex justify-center">
              <Loader2 size={20} className="animate-spin text-blue-500" />
            </div>
          )}

          {hasResults && (
            <div className="overflow-x-auto px-5 pb-4">
              {/* Table header */}
              <div className="grid gap-0 py-3 border-b border-gray-100 dark:border-slate-800"
                style={{ gridTemplateColumns: '1.8fr 1.2fr 1.4fr 0.6fr 0.7fr' }}>
                {['API', 'Branch', 'Build Date', 'Env', 'Status'].map((h) => (
                  <span key={h} className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-slate-500"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {h}
                  </span>
                ))}
              </div>

              {/* API rows */}
              {tenant.tenantApis?.map((ta, i) => {
                const r = results[ta.id];
                const branch = r?.success ? stripBranch(r.data?.buildInfo?.sourceBranch) : null;
                const rowStatus = r?.success ? 'healthy' : r ? 'down' : null;
                const branchColor = branch && hasMismatch && branch !== topBranch ? '#f97316' : undefined;

                return (
                  <div
                    key={ta.id}
                    className="grid items-center py-3 hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors"
                    style={{
                      gridTemplateColumns: '1.8fr 1.2fr 1.4fr 0.6fr 0.7fr',
                      borderBottom: i < (tenant.tenantApis?.length ?? 0) - 1 ? '1px solid #f9fafb' : 'none',
                    }}
                  >
                    <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-200"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      {ta.apiName}
                    </span>
                    <span>
                      {branch ? <BranchBadge branch={branch} color={branchColor} /> : '—'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-slate-400" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {r?.success ? formatDate(r.data?.buildInfo?.buildDate) : '—'}
                    </span>
                    <span className="text-xs font-semibold" style={{ fontFamily: "'JetBrains Mono', monospace", color }}>
                      {r?.success ? r.data?.deployment?.environment : '—'}
                    </span>
                    <span>
                      {!r && refreshing ? (
                        <Loader2 size={14} className="animate-spin text-blue-400" />
                      ) : rowStatus ? (
                        <StatusLabel status={rowStatus} />
                      ) : (
                        <span className="text-gray-300 dark:text-slate-600">—</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function HealthCheck() {
  const [apis, setApis] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [probeResults, setProbeResults] = useState({});   // { [tenantId]: { [tenantApiId]: ProbeResult } }
  const [refreshingIds, setRefreshingIds] = useState({}); // { [tenantId]: boolean }
  const [showApiModal, setShowApiModal] = useState(false);
  const [tenantForm, setTenantForm] = useState(null);     // null | {} (new) | { ...tenant } (edit)
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  // New UI state
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'issues'

  const loadApis = useCallback(async () => {
    const data = await getApis().catch(() => []);
    setApis(data);
    return data;
  }, []);

  const loadTenants = useCallback(async () => {
    const data = await getTenants().catch(() => []);
    setTenants(data);
    return data;
  }, []);

  const handleRefreshTenant = useCallback(async (tenant) => {
    setRefreshingIds((s) => ({ ...s, [tenant.id]: true }));
    const apiEntries = tenant.tenantApis ?? [];
    const calls = apiEntries.map((ta) =>
      probe(ta.url).then((r) => ({ taId: ta.id, result: r })).catch((e) => ({
        taId: ta.id,
        result: { success: false, data: null, error: e.message },
      }))
    );
    const settled = await Promise.all(calls);
    const resultMap = {};
    settled.forEach(({ taId, result }) => { resultMap[taId] = result; });
    setProbeResults((s) => ({ ...s, [tenant.id]: resultMap }));
    setRefreshingIds((s) => ({ ...s, [tenant.id]: false }));
  }, []);

  useEffect(() => {
    Promise.all([loadApis(), loadTenants()])
      .then(([, loadedTenants]) => {
        // Auto-expand all on initial load
        setExpandedIds(new Set(loadedTenants.map((t) => t.id)));
        loadedTenants.forEach((tenant) => handleRefreshTenant(tenant));
      })
      .finally(() => setLoading(false));
  }, [loadApis, loadTenants, handleRefreshTenant]);

  const handleDeleteTenant = useCallback(async (id) => {
    setDeletingId(id);
    try {
      await deleteTenant(id);
      await loadTenants();
      setProbeResults((s) => { const c = { ...s }; delete c[id]; return c; });
    } finally {
      setDeletingId(null);
    }
  }, [loadTenants]);

  const handleTenantSaved = useCallback(async () => {
    setTenantForm(null);
    await loadTenants();
  }, [loadTenants]);

  // Expand/collapse helpers
  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const expandAll = () => setExpandedIds(new Set(tenants.map((t) => t.id)));
  const collapseAll = () => setExpandedIds(new Set());

  // Compute global stats
  const totalApis = tenants.reduce((s, t) => s + (t.tenantApis?.length ?? 0), 0);
  const allResults = Object.values(probeResults).flatMap((r) => Object.values(r));
  const healthyApis = allResults.filter((r) => r?.success).length;
  const downApis = allResults.filter((r) => r && !r.success).length;

  // Filter tenants
  const filtered = tenants.filter((t) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q
      || t.name.toLowerCase().includes(q)
      || (t.tenantApis ?? []).some((ta) => ta.apiName?.toLowerCase().includes(q));

    if (!matchesSearch) return false;
    if (filterStatus === 'issues') {
      const results = probeResults[t.id];
      if (!results) return false;
      return Object.values(results).some((r) => r && !r.success);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 size={28} className="animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>

      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-gray-200 dark:border-slate-700">
        <div className="max-w-[1280px] mx-auto flex items-center justify-between h-[68px] px-10">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: '0 2px 8px rgba(99,102,241,0.3)' }}>
              <HeartPulse size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-extrabold text-gray-900 dark:text-gray-100 tracking-tight">Health Check</h1>
              <p className="text-xs text-gray-400 dark:text-slate-500 font-medium -mt-0.5">API versions & deployment branches</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setShowApiModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-[13px] font-semibold border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 hover:border-gray-300 dark:hover:border-slate-600 transition-all"
            >
              <Settings size={15} />
              Manage APIs
            </button>
            <button
              onClick={() => setTenantForm({})}
              className="flex items-center gap-2 px-4 py-2.5 text-[13px] font-semibold text-white rounded-xl transition-all"
              style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: '0 2px 8px rgba(99,102,241,0.3)' }}
            >
              <Plus size={14} />
              Add Environment
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-10 py-7">
        {/* Stats row */}
        <div className="grid grid-cols-4 gap-3.5 mb-7">
          {[
            { label: 'Total APIs', value: totalApis, icon: '📡', color: '#6366f1', bg: '#eef2ff' },
            { label: 'Healthy', value: healthyApis, icon: '✓', color: '#22c55e', bg: '#f0fdf4' },
            { label: 'Down', value: downApis, icon: '✕', color: '#ef4444', bg: '#fef2f2' },
            { label: 'Environments', value: tenants.length, icon: '🌐', color: '#0ea5e9', bg: '#f0f9ff' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 px-5 py-4 flex items-center gap-3.5">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center text-lg font-extrabold shrink-0"
                style={{ background: stat.bg, color: stat.color }}
              >
                {stat.icon}
              </div>
              <div>
                <div className="text-2xl font-extrabold text-gray-900 dark:text-gray-100 leading-none tracking-tight">{stat.value}</div>
                <div className="text-xs text-gray-400 dark:text-slate-500 font-semibold mt-0.5">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2.5 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-[360px]">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder="Search environments, APIs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-[13px] font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
              />
            </div>

            {/* Filter pills */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded-xl p-0.5">
              {[
                { key: 'all', label: 'All' },
                { key: 'issues', label: 'Issues' },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilterStatus(f.key)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    filterStatus === f.key
                      ? 'bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="px-3.5 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3.5 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
            >
              Collapse All
            </button>
          </div>
        </div>

        {/* Empty state */}
        {tenants.length === 0 && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-700 p-16 text-center">
            <HeartPulse size={40} className="mx-auto text-gray-300 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">No environments yet</h3>
            <p className="text-sm text-gray-400 dark:text-slate-500 mb-6">
              Start by adding APIs to the catalog, then create an environment with their endpoints.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowApiModal(true)}
                className="px-4 py-2.5 text-sm border border-gray-300 dark:border-slate-600 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 font-medium dark:text-gray-300"
              >
                Manage APIs
              </button>
              <button
                onClick={() => setTenantForm({})}
                className="px-4 py-2.5 text-sm font-semibold text-white rounded-xl"
                style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
              >
                Add Environment
              </button>
            </div>
          </div>
        )}

        {/* Environment cards — single column */}
        {filtered.length > 0 && (
          <div className="flex flex-col gap-3.5">
            {filtered.map((tenant, idx) => (
              <EnvCard
                key={tenant.id}
                tenant={tenant}
                color={ENV_COLORS[idx % ENV_COLORS.length]}
                isExpanded={expandedIds.has(tenant.id)}
                onToggle={() => toggleExpand(tenant.id)}
                onEdit={(t) => setTenantForm(t)}
                onDelete={handleDeleteTenant}
                onRefresh={() => handleRefreshTenant(tenant)}
                probeResults={probeResults[tenant.id]}
                refreshing={refreshingIds[tenant.id] ?? false}
              />
            ))}
          </div>
        )}

        {/* No results from search/filter */}
        {tenants.length > 0 && filtered.length === 0 && (
          <div className="text-center py-16">
            <Search size={40} className="mx-auto text-gray-300 dark:text-slate-600 mb-3" />
            <p className="text-base font-semibold text-gray-500 dark:text-slate-400">No environments found</p>
            <p className="text-sm text-gray-400 dark:text-slate-500 mt-1">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showApiModal && (
        <ManageApisModal
          apis={apis}
          onClose={() => setShowApiModal(false)}
          onRefreshApis={loadApis}
        />
      )}

      {tenantForm !== null && (
        <TenantFormModal
          apis={apis}
          existing={tenantForm?.id ? tenantForm : null}
          onClose={() => setTenantForm(null)}
          onSaved={handleTenantSaved}
        />
      )}

      {/* Delete overlay */}
      {deletingId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <Loader2 size={32} className="animate-spin text-white" />
        </div>
      )}
    </div>
  );
}
